// frontend/src/pages/DeliveryNotesPage.js
//
// Bons de livraison — the document signed when a car goes back to its owner.
//
// It is the counterpart to the fiche de réception: that records the state the
// car arrived in, this records the state it left in. The pair is what answers
// "that scratch wasn't there when I dropped it off" three weeks later, and
// without the second half the first half proves nothing.
//
// The PDF comes from the server rather than being built here, so the copy the
// workshop prints and the copy the client opens from their SMS are byte for
// byte the same document — the receipt has a browser generator and a server
// one kept in step by hand, and that is a standing source of drift.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import {
  Page, Panel, Badge, Table, Tr, Td, Button, Field, Modal, Checkbox,
  EmptyState, Loading, ErrorNote, Note, inputClass, textareaClass,
} from '../components/ui/Iparc';
import SearchableSelect from '../components/SearchableSelect';
import { fmt } from '../theme/iparc';

// datetime-local wants LOCAL wall-clock, not UTC. toISOString() would shift
// the value by the timezone offset and show the wrong hour in the picker.
const nowLocal = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    + `T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const EMPTY = {
  vehicle: '', plateNumber: '', vehicleLabel: '', vin: '',
  clientName: '', clientPhone: '',
  receivedByName: '', receivedByPhone: '',
  deliveredAt: nowLocal(),
  odometerReading: '', fuelLevel: '', keysReturned: '1', personalItems: '',
  workPerformed: '', reserves: '', notes: '', sendSms: true,
};

const fmtTime = (d) => {
  if (!d) return '';
  const t = new Date(d);
  return Number.isNaN(t.getTime())
    ? ''
    : t.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  return Number.isNaN(t.getTime())
    ? '—'
    : t.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function DeliveryNotesPage({ token: propToken }) {
  const { token: ctxToken, user } = useAuth();
  const token = propToken || ctxToken;

  const [notes, setNotes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadNotes = useCallback(async () => {
    const res = await fetch(`${API_URL}/delivery-notes`, { headers: auth });
    if (!res.ok) throw new Error('Chargement des bons de livraison impossible');
    return res.json();
  }, [auth]);

  useEffect(() => {
    if (!token) { setError('Session expirée — reconnectez-vous.'); setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const list = await loadNotes();
        if (alive) setNotes(Array.isArray(list) ? list : []);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, loadNotes]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/vehicles/all`, { headers: auth })
      .then((r) => (r.ok ? r.json() : []))
      .then((v) => setVehicles(Array.isArray(v) ? v : []))
      .catch(() => { });
  }, [token, auth]);

  // Picking the car fills in everything already on record — plate, VIN, owner
  // and the last known reading. The counter should be typing the two things
  // only it knows: who is taking the car, and in what state.
  const pickVehicle = (id) => {
    const v = vehicles.find((x) => x._id === id);
    setForm((f) => ({
      ...f,
      vehicle: id,
      plateNumber: v?.plateNumber || '',
      vehicleLabel: [v?.brand || v?.make, v?.model].filter(Boolean).join(' '),
      vin: v?.vin || '',
      clientName: f.clientName || v?.owner?.name || '',
      odometerReading: f.odometerReading || (v?.odometerReading ? String(v.odometerReading) : ''),
    }));
  };

  const set = (k) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const selectedVehicle = vehicles.find((v) => v._id === form.vehicle);
  const lastKm = Number(selectedVehicle?.odometerReading) || 0;
  const enteredKm = Number(form.odometerReading);
  const kmBackwards = form.odometerReading !== ''
    && Number.isFinite(enteredKm) && lastKm > 0 && enteredKm < lastKm;

  const canSave = form.clientName.trim() && form.receivedByName.trim()
    && form.plateNumber.trim() && !saving;

  const openPdf = async (id) => {
    // Fetched rather than linked so the Authorization header goes with it —
    // the endpoint is party-scoped, not public.
    try {
      const res = await fetch(`${API_URL}/delivery-notes/${id}/pdf`, { headers: auth });
      if (!res.ok) throw new Error('Téléchargement impossible');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(e.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError('');
    setOkMsg('');
    try {
      const res = await fetch(`${API_URL}/delivery-notes`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          vehicle: form.vehicle || undefined,
          // The datetime-local value is local wall-clock with no zone, which
          // new Date() reads as local — so the moment stored is the moment the
          // keys changed hands, not a midday stand-in.
          deliveredAt: form.deliveredAt,
          workPerformed: form.workPerformed
            .split('\n').map((s) => s.trim()).filter(Boolean),
          sendSms: form.sendSms && !!(form.receivedByPhone.trim() || form.clientPhone.trim()),
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Enregistrement impossible');

      setNotes((prev) => [body, ...prev]);
      setOkMsg(`Bon de livraison ${body.documentNumber} enregistré.`);
      setForm({ ...EMPTY, deliveredAt: nowLocal() });
      setShowForm(false);
      openPdf(body._id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelling) return;
    try {
      const res = await fetch(`${API_URL}/delivery-notes/${cancelling._id}/cancel`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Annulation impossible');
      setNotes((prev) => prev.map((n) => (n._id === body._id ? body : n)));
      setOkMsg(`Bon ${body.documentNumber} annulé.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(null);
      setCancelReason('');
    }
  };

  const live = notes.filter((n) => !n.cancelledAt);
  const thisMonth = live.filter((n) => {
    const d = new Date(n.deliveredAt);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  return (
    <Page
      subtitle="Atelier"
      title="Bons de livraison"
      actions={
        <Button
          variant="primary"
          onClick={() => {
            // Read the clock on open, not at module load: a page left open all
            // morning would otherwise default to the hour it was loaded.
            if (!showForm) setForm((f) => ({ ...f, deliveredAt: nowLocal() }));
            setShowForm((v) => !v);
            setOkMsg('');
          }}
        >
          {showForm ? 'Fermer' : 'Nouveau bon'}
        </Button>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      {okMsg && <Note tone="ok">{okMsg}</Note>}

      <Panel>
        <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] mb-1.5">
          Véhicules remis ce mois
        </p>
        <p className="font-['Space_Grotesk',sans-serif] tabular-nums text-[30px] font-semibold tracking-[-.02em] leading-none text-[#74A98D]">
          {thisMonth}
        </p>
        <p className="text-[11.5px] text-[#8F877B] mt-2">
          {live.length === 0
            ? 'Aucun bon de livraison émis pour le moment.'
            : `${live.length} bon${live.length > 1 ? 's' : ''} émis au total.`}
        </p>
      </Panel>

      {showForm && (
        <Panel title="Nouveau bon de livraison">
          <form onSubmit={submit} className="flex flex-col gap-3.5">
            <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
              <Field label="Véhicule" hint="Renseigne la plaque, le VIN et le propriétaire">
                <SearchableSelect
                  options={vehicles.map((v) => ({
                    value: v._id,
                    label: `${v.plateNumber} — ${[v.brand || v.make, v.model].filter(Boolean).join(' ')}`.trim(),
                    sublabel: v.vin ? `VIN ${String(v.vin).toUpperCase()}` : undefined,
                  }))}
                  value={form.vehicle}
                  onChange={pickVehicle}
                  placeholder="Rechercher un véhicule…"
                  emptyLabel="Aucun véhicule rattaché à votre atelier."
                />
              </Field>

              <Field label="Plaque d'immatriculation" required>
                <input className={inputClass} value={form.plateNumber}
                       onChange={set('plateNumber')} placeholder="AB 1234 CD" required />
              </Field>

              <Field label="Véhicule (marque / modèle)">
                <input className={inputClass} value={form.vehicleLabel}
                       onChange={set('vehicleLabel')} placeholder="Toyota Hilux" />
              </Field>

              <Field label="Client" required>
                <input className={inputClass} value={form.clientName}
                       onChange={set('clientName')} placeholder="Nom du propriétaire" required />
              </Field>

              <Field label="Téléphone du client">
                <input className={inputClass} value={form.clientPhone}
                       onChange={set('clientPhone')} placeholder="07 00 00 00 00" />
              </Field>

              <Field
                label="Repris par"
                required
                hint="Souvent un chauffeur ou un proche, pas le propriétaire"
              >
                <input className={inputClass} value={form.receivedByName}
                       onChange={set('receivedByName')} placeholder="Nom et prénom" required />
              </Field>

              <Field label="Téléphone de la personne">
                <input className={inputClass} value={form.receivedByPhone}
                       onChange={set('receivedByPhone')} placeholder="07 00 00 00 00" />
              </Field>

              <Field label="Date et heure de remise" required hint="Le moment réel de la remise des clés">
                <input type="datetime-local" max={nowLocal()} className={inputClass}
                       value={form.deliveredAt} onChange={set('deliveredAt')} required />
              </Field>

              <Field
                label="Kilométrage à la remise"
                hint={lastKm > 0 ? `Dernier relevé : ${fmt(lastKm)} km` : undefined}
              >
                <input type="number" min="0" className={inputClass}
                       value={form.odometerReading} onChange={set('odometerReading')} />
                {kmBackwards && (
                  <span className="block text-[10.5px] text-[#E2574B] mt-1">
                    Inférieur au dernier relevé ({fmt(lastKm)} km) — vérifiez la saisie.
                  </span>
                )}
              </Field>

              <Field label="Carburant (%)" hint="Même échelle que la fiche de réception">
                <input type="number" min="0" max="100" className={inputClass}
                       value={form.fuelLevel} onChange={set('fuelLevel')} placeholder="0 – 100" />
              </Field>

              <Field label="Clés remises">
                <input type="number" min="0" max="5" className={inputClass}
                       value={form.keysReturned} onChange={set('keysReturned')} />
              </Field>

              <Field label="Effets personnels restitués">
                <input className={inputClass} value={form.personalItems}
                       onChange={set('personalItems')} placeholder="Cric, gilet, documents…" />
              </Field>
            </div>

            <Field label="Travaux effectués" hint="Une ligne par intervention">
              <textarea rows={3} className={textareaClass} value={form.workPerformed}
                        onChange={set('workPerformed')}
                        placeholder={'Vidange + filtre à huile\nPlaquettes avant'} />
            </Field>

            <Field
              label="Réserves du client"
              hint="Laissez vide si le client n'a rien à signaler — la mention sera imprimée"
            >
              <textarea rows={2} className={textareaClass} value={form.reserves}
                        onChange={set('reserves')}
                        placeholder="Rayure constatée sur l'aile avant droite…" />
            </Field>

            <Field label="Note">
              <textarea rows={2} className={textareaClass} value={form.notes}
                        onChange={set('notes')} placeholder="Revenir dans 5 000 km" />
            </Field>

            <Checkbox
              checked={form.sendSms && !!(form.receivedByPhone.trim() || form.clientPhone.trim())}
              disabled={!(form.receivedByPhone.trim() || form.clientPhone.trim())}
              onChange={set('sendSms')}
              label="Envoyer le bon de livraison par SMS"
              hint={
                (form.receivedByPhone.trim() || form.clientPhone.trim())
                  ? 'Le client reçoit un lien vers le PDF signé.'
                  : 'Renseignez un téléphone pour pouvoir envoyer le SMS.'
              }
            />

            <div className="flex justify-end gap-2.5">
              <Button type="button" onClick={() => { setShowForm(false); setForm({ ...EMPTY, deliveredAt: nowLocal() }); }}>
                Annuler
              </Button>
              <Button type="submit" variant="primary" disabled={!canSave}>
                {saving ? 'Enregistrement…' : 'Enregistrer et imprimer'}
              </Button>
            </div>
          </form>
        </Panel>
      )}

      <Panel title="Bons émis" bodyClass="p-0">
        {loading ? (
          <Loading label="Chargement…" />
        ) : notes.length === 0 ? (
          <EmptyState
            title="Aucun bon de livraison"
            hint="Émettez-en un à chaque véhicule rendu : c'est la preuve de l'état du véhicule au moment de la remise."
            action={<Button variant="primary" onClick={() => setShowForm(true)}>Nouveau bon</Button>}
          />
        ) : (
          <Table
            minWidth={920}
            head={['N°', 'Date', 'Véhicule', 'Client', 'Repris par', 'Km', 'État', '']}
          >
            {notes.map((n) => {
              const cancelled = !!n.cancelledAt;
              return (
                <Tr key={n._id} className={cancelled ? 'opacity-55' : ''}>
                  <Td mono>{n.documentNumber}</Td>
                  <Td>
                    {fmtDate(n.deliveredAt)}
                    <span className="block text-[11px] text-[#6E665C] mt-0.5 font-['Space_Grotesk',sans-serif] tabular-nums">
                      {fmtTime(n.deliveredAt)}
                    </span>
                  </Td>
                  <Td>
                    <span className="font-['Space_Grotesk',sans-serif] tracking-[.02em] text-[#F4F1EA]">
                      {n.plateNumber || '—'}
                    </span>
                    {n.vehicleLabel && (
                      <span className="block text-[11px] text-[#6E665C] mt-0.5">{n.vehicleLabel}</span>
                    )}
                  </Td>
                  <Td className="text-[#F4F1EA]">
                    {n.clientName}
                    {n.clientPhone && (
                      <span className="block text-[11px] text-[#6E665C] mt-0.5">
                        {n.clientPhone}
                        {n.smsSentAt && <span className="text-[#74A98D] ml-1.5">SMS ✓</span>}
                      </span>
                    )}
                  </Td>
                  <Td>{n.receivedByName || '—'}</Td>
                  <Td mono className="text-right">
                    {n.odometerReading ? fmt(n.odometerReading) : '—'}
                  </Td>
                  <Td>
                    <Badge tone={cancelled ? 'neutral' : 'ok'}>
                      {cancelled ? 'Annulé' : 'Livré'}
                    </Badge>
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    <Button variant="quiet" className="h-[26px] px-2"
                            onClick={() => openPdf(n._id)} title="Ouvrir le PDF">
                      PDF
                    </Button>
                    {!cancelled && (
                      <Button
                        variant="quiet"
                        className="h-[26px] px-2 text-[#8F877B] hover:text-[#E2574B]"
                        onClick={() => { setCancelling(n); setCancelReason(''); }}
                      >
                        Annuler
                      </Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Table>
        )}
      </Panel>

      <Modal
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        subtitle={cancelling?.documentNumber}
        title="Annuler ce bon de livraison"
        footer={
          <>
            <Button onClick={() => setCancelling(null)}>Retour</Button>
            <Button variant="danger" onClick={confirmCancel}>Confirmer l'annulation</Button>
          </>
        }
      >
        <p className="text-[12.5px] text-[#C9C3B6]">
          Le bon est conservé et marqué annulé — il n'est jamais supprimé, pour qu'il ne
          manque aucun numéro dans la série. Le client garde sa copie.
        </p>
        <Field label="Motif">
          <input className={inputClass} value={cancelReason}
                 onChange={(e) => setCancelReason(e.target.value)}
                 placeholder="Erreur de saisie, véhicule non repris…" />
        </Field>
      </Modal>
    </Page>
  );
}
